import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrap from '../components/PageWrap';
import useDocumentMeta from '../hooks/useDocumentMeta';
import routeMetaFor from '../utils/routeMeta';
import { SUPPORT_EMAIL } from '../data/siteContent';
import { APP_LEGAL, EFFECTIVE_DATE, PUBLISHER } from '../data/legalContent';

/**
 * TickleApps legal directory (task T05).
 *
 * This page deliberately makes no studio-wide data-practice claim: each app's
 * own notice governs that app, and this page only identifies the publisher and
 * points to those notices. It must not claim compliance with a named law,
 * a response deadline, or collection practices for products not listed here.
 */
const APP_ROUTES = [
  {
    app: APP_LEGAL.nutriscan.name,
    version: APP_LEGAL.nutriscan.version,
    links: [
      { to: '/apps/nutriscan/privacy', label: 'Privacy Policy' },
      { to: '/apps/nutriscan/terms', label: 'Terms of Service' },
      { to: '/apps/nutriscan/account-deletion', label: 'Account Deletion' },
    ],
  },
  {
    app: APP_LEGAL.cardcycle.name,
    version: APP_LEGAL.cardcycle.version,
    links: [
      { to: '/apps/cardcycle/privacy', label: 'Privacy Notice' },
      { to: '/apps/cardcycle/terms', label: 'Terms of Use' },
      { to: '/apps/cardcycle/account-deletion', label: 'Data Deletion' },
    ],
  },
];

export default function Privacy() {
  useDocumentMeta(routeMetaFor('/privacy'));

  return (
    <PageWrap testId="privacy-page">
      <Navbar />

      <section className="page-hero">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow reveal">Legal</div>
          <h1 className="reveal d1">Legal directory</h1>
          <p className="lead reveal d2" data-testid="legal-directory-lead">
            Each TickleApps product has its own privacy notice, terms, and deletion page. Those pages govern that product.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <p className="reveal" data-testid="legal-directory-publisher" style={{ marginBottom: 14, maxWidth: '760px' }}>
            TickleApps products are published by <strong>{PUBLISHER}</strong>. For any question about a product&apos;s legal
            pages, email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
          <p className="reveal d1" style={{ marginBottom: 40, maxWidth: '760px' }}>
            This page is a directory. It is not legal advice, and it is not a data-practices policy for the website or for
            products that are not listed here.
          </p>

          <div className="grid-2">
            {APP_ROUTES.map((entry, i) => (
              <div className={`card reveal d${i + 1}`} key={entry.app} data-testid={`legal-directory-${entry.app.toLowerCase()}`}>
                <h3>{entry.app}</h3>
                <p style={{ marginTop: 6, fontSize: '0.85rem', color: 'var(--text-3)' }}>
                  {entry.version} · Effective {EFFECTIVE_DATE}
                </p>
                <ul style={{ marginTop: 16 }}>
                  {entry.links.map(link => (
                    <li key={link.to} style={{ padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: '0.92rem' }}>
                      <Link to={link.to}>{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="reveal d2" style={{ marginTop: 40, maxWidth: '760px' }}>
            To make a data-protection request, email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and say which
            app your request relates to. The <Link to="/dpdpa">data-protection requests</Link> page explains what to include.
          </p>
        </div>
      </section>

      <Footer />
    </PageWrap>
  );
}
